import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

import api from '../../../services/api';
import { getExtraBalances, deleteExtraBalance } from '../../../services/leaveExtraBalanceService';
import PersonCell from '../../../components/ui/PersonCell';
import { extractListData, formatDate } from '../../../utils/Utils';

// Çalışan seçici — continuous-feedback/config'teki buildEmployeeAsync'in BAĞIMSIZ
// kopyası (repo kuralı: farklı sayfa grupları yardımcı paylaşmaz). `fullName[like]`
// EmployeeController'ın gerçekten bağladığı arama anahtarıdır (`name[like]` no-op).
// Dönen kimlik Employee.Id — ek izin satırı kişiye Employee.Id ile yazılır.
export function buildEmployeeAsync() {
  const etiket = (e) => e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim();
  return {
    onSearch: async (q) => {
      const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      return extractListData(res).map((e) => ({ id: e.id, name: etiket(e), profilePicture: e.profilePicture }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      return e ? { id: e.id, name: etiket(e), profilePicture: e.profilePicture } : null;
    },
  };
}

export const employeeAsync = buildEmployeeAsync();

// "yyyy-MM-dd" — DateTimePicker'ın date kipinin beklediği ve gönderdiği biçim.
// Sunucu ISO tarih döner ("2026-03-15T00:00:00Z"); saat dilimine çevirmeden ilk
// 10 karakteri alınır — çevirmek UTC gece yarısını bir önceki güne kaydırırdı.
export const toDateInput = (iso) => (typeof iso === 'string' && iso.length >= 10 ? iso.slice(0, 10) : '');

export const todayInput = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const g = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${g}`;
};

export const toFormValues = (row) => ({
  employeeId: row.employeeId,
  leaveVariationId: row.leaveVariationId,
  days: row.days,
  effectiveDate: toDateInput(row.effectiveDate),
  note: row.note || '',
});

// Formdan istek gövdesi (LeaveExtraBalanceRequest). Gün metin kutusundan gelir;
// virgüllü giriş ("1,5") de kabul edilir.
export const toRequest = (form) => ({
  employeeId: form.employeeId,
  leaveVariationId: form.leaveVariationId || null,
  days: Number(String(form.days ?? '').replace(',', '.')),
  effectiveDate: form.effectiveDate || null,
  note: (form.note || '').trim(),
});

export const DATE_UTC = {
  year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC',
};

// Bir GÜN değerini (geçerlilik tarihi) basar: ISO metnin ilk 10 karakteri UTC gece
// yarısı olarak okunur ve UTC'de biçimlendirilir — ne sunucunun ne tarayıcının saat
// dilimi günü kaydırabilir.
export const formatEffectiveDate = (value, lang) => {
  const gun = toDateInput(value);
  return gun ? formatDate(gun, lang, DATE_UTC) : '-';
};

// AKM POC §6.5 — kaynak: elle girilen satır (sourceType null) ya da puantajdan doğan otomatik kredi
// (nöbet / 657 fazla çalışma karşılığı; sourceKey "yyyy-MM-dd" = kredinin günü). Otomatik satırlar
// salt okunurdur (sunucu: 409 leaveExtraBalance.autoCreditReadOnly).
export const SOURCE_KEYS = {
  dutyShift: 'leaveExtraBalance.sourceDutyShift',
  overtimeCompensation: 'leaveExtraBalance.sourceOvertimeCompensation',
};

export const sourceLabel = (row, t, lang) => {
  const key = row?.sourceType ? SOURCE_KEYS[row.sourceType] : null;
  if (!key) return t('leaveExtraBalance.sourceManual');
  return t(key, { date: formatEffectiveDate(row.sourceKey, lang) });
};

export const isAutomaticRow = (row) => !!row?.isAutomatic;

const currentYear = new Date().getFullYear();
// Gelecek yıl da seçilebilir (sunucu bir yıl ileriye kadar tarih kabul eder).
export const YEAR_OPTIONS = Array.from({ length: 7 }, (_, i) => {
  const y = currentYear + 1 - i;
  return { id: y, name: String(y) };
});

const useLeaveExtraBalanceApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [failed, setFailed] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const istekSirasi = useRef(0);

  useEffect(() => {
    const sira = istekSirasi.current + 1;
    istekSirasi.current = sira;
    setFetching(true);
    getExtraBalances(queryParams)
      .then((res) => {
        if (sira !== istekSirasi.current) return;   // eski süzgecin geç gelen yanıtı
        // api.js GET okumalarında reject ETMEZ — gerçek sinyal res.data.status.
        if (!res?.data?.status) {
          setFailed(true);
          setData([]);
          setPagination({ totalRowCount: 0, pageIndex: 0, pageSize: queryParams.pageSize || 10 });
          return;
        }
        setFailed(false);
        const sonuc = res.data.data;
        const items = extractListData(res);
        setData(items);
        setPagination({
          totalRowCount: sonuc?.totalRowCount ?? items.length,
          pageIndex: sonuc?.pageIndex ? sonuc.pageIndex - 1 : 0,
          pageSize: sonuc?.pageSize || queryParams.pageSize || 10,
        });
      })
      .finally(() => { if (sira === istekSirasi.current) setFetching(false); });
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const remove = useCallback(async (id) => {
    try {
      await deleteExtraBalance(id);
      refresh();
      return true;
    } catch {
      return false;   // hata toast'ı interceptor'da
    }
  }, [refresh]);

  // Sunucu tarafı sayfalama; süzgeçler araç çubuğundaki seçicilerden gelir —
  // kolonlar kendi filtresini/sıralamasını TAŞIMAZ (DataTable'ın filtre diyaloğu
  // `column.name` gönderir ve sunucunun anahtarlarıyla eşleşmez).
  const columns = useMemo(() => [
    {
      name: 'fullName',
      label: t('leaveExtraBalance.employee'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <PersonCell name={v} profilePicture={row?.profilePicture} />;
        },
      },
    },
    {
      name: 'registrationNumber',
      label: t('leaveExtraBalance.registrationNumber'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'leaveVariationName',
      label: t('leaveExtraBalance.variation'),
      options: { filter: false, sort: false },
    },
    {
      name: 'days',
      label: t('leaveExtraBalance.days'),
      options: { filter: false, sort: false },
    },
    {
      name: 'effectiveDate',
      label: t('leaveExtraBalance.effectiveDate'),
      // Geçerlilik tarihi bir GÜNdür. ISO metnin TARİH kısmı basılır (formatEffectiveDate):
      // sunucu saat dilimine göre "…T00:00:00+03:00" da dönebilir (legacy timestamp) —
      // anı UTC'ye çevirmek o durumda bir önceki günü gösterirdi.
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => formatEffectiveDate(v, lang),
      },
    },
    {
      name: 'sourceType',
      label: t('leaveExtraBalance.source'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_, tableMeta) => sourceLabel(data[tableMeta.rowIndex], t, lang),
      },
    },
    {
      name: 'expiresOn',
      label: t('leaveExtraBalance.expiresOn'),
      // Son kullanım bir GÜNdür — geçerlilik tarihiyle aynı biçimleyici (gün kaymaz).
      options: { filter: false, sort: false, customBodyRender: (v) => formatEffectiveDate(v, lang) },
    },
    {
      name: 'note',
      label: t('leaveExtraBalance.note'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'createdByName',
      label: t('leaveExtraBalance.createdBy'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, meta) => {
          const row = data?.[meta?.rowIndex];
          if (row?.createdByApiKey && v) return t('apiKeys.actor', { name: v });
          return v || '-';
        },
      },
    },
    {
      name: 'createdAt',
      label: t('leaveExtraBalance.createdAt'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? formatDate(v, lang) : '-') },
    },
  ], [t, lang, data]);

  return {
    fetching, failed, data, pagination, columns, refresh, remove,
  };
};

export default useLeaveExtraBalanceApi;
